/**
 * AdminPageShell — wrapper auto-connecté à Redux + Router.
 *
 * Wrapper de l'admin auto-connecté à Redux + Router. Sidebar + topbar + main
 * câblés automatiquement (user Redux, location, logout, dark mode).
 *
 * Layout : full-width (back office, on exploite tout l'espace dispo).
 * Padding intérieur léger, configurable via `padded={false}` pour les
 * pages qui veulent gérer leur padding elles-mêmes (ex: éditeur TipTap).
 *
 * Breadcrumb : auto-dérivé depuis location.pathname via BREADCRUMB_NODES.
 * Pour personnaliser le dernier label (ex : titre de l'article édité),
 * passer la prop `breadcrumbLast`. Pour un breadcrumb totalement custom,
 * passer la prop `breadcrumb` (array d'items ou JSX).
 */

import React from "react";
import { Link, withRouter } from "react-router-dom";
import { connect } from "react-redux";

import { logoutUser } from "../../actions/authActions";
import { toggleDarkMode } from "../../actions/preferenceActions";
import confirmAlert from "../../utils/confirmAlert";
import AdminCommandPalette from "./AdminCommandPalette";
import AdminNotifications from "./AdminNotifications";

import { AdminAppShell, Breadcrumb } from "../../design-system";
import { buildAdminBreadcrumb } from "../../utils/adminBreadcrumb";

function AdminPageShellRaw({
  user,
  _preferences,
  location,
  history,
  logoutUser,
  toggleDarkMode,
  pageTitle,
  breadcrumb,
  breadcrumbLast,
  topbarCenter,
  topbarRight,
  counts,
  padded = true,
  children,
}) {
  const onLogout = () =>
    confirmAlert({
      title: "Déconnexion",
      message: "Êtes-vous sûr de vouloir vous déconnecter ?",
      type: "warning",
      confirmText: "Déconnexion",
      cancelText: "Annuler",
      onConfirm: () => {
        logoutUser();
        history.push("/login");
      },
    });

  const onToggleDarkMode = () => toggleDarkMode(!_preferences?._dark);

  const defaultTopbarCenter = <AdminCommandPalette />;
  const defaultTopbarRight = <AdminNotifications />;

  // Si la page passe un JSX custom, on l'utilise tel quel.
  // Sinon, si elle passe une array, on rend avec le composant DS.
  // Sinon, auto-derivation depuis l'URL.
  let breadcrumbNode;
  if (React.isValidElement(breadcrumb)) {
    breadcrumbNode = breadcrumb;
  } else {
    const items = Array.isArray(breadcrumb)
      ? breadcrumb
      : buildAdminBreadcrumb(location.pathname, breadcrumbLast);
    breadcrumbNode = <Breadcrumb items={items} LinkComponent={Link} />;
  }

  return (
    <AdminAppShell
      user={user}
      currentPath={location.pathname}
      isDarkMode={_preferences?._dark || false}
      onToggleDarkMode={onToggleDarkMode}
      onLogout={onLogout}
      LinkComponent={Link}
      pageTitle={pageTitle}
      breadcrumb={breadcrumbNode}
      topbarCenter={topbarCenter !== undefined ? topbarCenter : defaultTopbarCenter}
      topbarRight={topbarRight !== undefined ? topbarRight : defaultTopbarRight}
      counts={counts}
    >
      {padded ? (
        <div className="w-full px-5 sm:px-6 lg:px-8 xl:px-10 py-6 lg:py-8">{children}</div>
      ) : (
        children
      )}
    </AdminAppShell>
  );
}

const mapStateToProps = (state) => ({
  user: state.auth.user,
  _preferences: state._preferences,
});

const AdminPageShell = connect(mapStateToProps, { logoutUser, toggleDarkMode })(
  withRouter(AdminPageShellRaw),
);

export default AdminPageShell;
